import { passkeyClient } from "@better-auth/passkey/client";
import {
  adminClient,
  multiSessionClient,
  organizationClient,
  usernameClient,
} from "better-auth/client/plugins";
import {
  authRequest,
  createFluentryAuthClient,
  type AuthClientLike,
} from "@fluentry/shared-workbench/auth";
import { resolveMobileBridgeConfig } from "./mobile-bridge";

// The authenticated session shape product-app reads from Better Auth. Defined here (not in any mock module)
// so the runtime auth path never depends on test-only fixtures.
export type ProductAppAuthSessionData = {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
  session: {
    id: string;
    activeOrganizationId: string | null;
  };
} | null;

function isLocalHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "lvh.me" ||
    hostname.endsWith(".localhost") ||
    hostname.endsWith(".local") ||
    hostname.endsWith(".lvh.me")
  );
}

function normalizeOrigin(origin: string): string {
  return origin.replace(/\/+$/, "");
}

export function resolveProductAppApiBaseUrl(
  runtimeOrigin = typeof window !== "undefined" ? window.location.origin : "",
) {
  if (runtimeOrigin.startsWith("http://") || runtimeOrigin.startsWith("https://")) {
    const hostname = new URL(runtimeOrigin).hostname;
    if (!isLocalHostname(hostname)) {
      // Deployed web surfaces should stay on same-origin /api so the edge worker can bridge to api-core over a Service binding.
      return normalizeOrigin(runtimeOrigin);
    }
  }

  return resolveMobileBridgeConfig(runtimeOrigin).apiOrigin;
}

export type ProductAppAuthClient = AuthClientLike & {
  useSession: () => {
    data: ProductAppAuthSessionData;
    isPending: boolean;
    refetch: () => Promise<void>;
  };
  signOut: () => Promise<unknown>;
  signIn: {
    email: (payload: Record<string, unknown>) => Promise<{ error: { message?: string } | null }>;
    passkey: (payload: Record<string, unknown>) => Promise<{ error: { message?: string } | null }>;
  };
  signUp: {
    email: (payload: Record<string, unknown>) => Promise<{ error: { message?: string } | null }>;
  };
  passkey: {
    addPasskey: (
      payload: Record<string, unknown>,
    ) => Promise<{ error: { message?: string } | null }>;
  };
};

const authClientCache = new Map<string, ProductAppAuthClient>();

export function getProductAppAuthClient(
  runtimeOrigin = typeof window !== "undefined" ? window.location.origin : "",
): ProductAppAuthClient {
  const apiBaseUrl = resolveProductAppApiBaseUrl(runtimeOrigin);
  const cached = authClientCache.get(apiBaseUrl);
  if (cached) {
    return cached;
  }

  // Resolve lazily so tests can override runtime URLs before the first Better Auth client is instantiated.
  const nextClient = createFluentryAuthClient({
    baseURL: apiBaseUrl,
    plugins: [
      adminClient(),
      usernameClient(),
      multiSessionClient(),
      organizationClient(),
      passkeyClient(),
    ],
  }) as unknown as ProductAppAuthClient;
  authClientCache.set(apiBaseUrl, nextClient);
  return nextClient;
}

export const authClient = new Proxy({} as ProductAppAuthClient, {
  get(_target, property, receiver) {
    return Reflect.get(getProductAppAuthClient(), property, receiver);
  },
}) as ProductAppAuthClient;

// Product-app shares this wrapper across screens so Better Auth RPC error handling stays identical everywhere.
export async function productAppAuthRequest<T>(
  path: string,
  init: Record<string, unknown>,
): Promise<T> {
  return authRequest<T>(getProductAppAuthClient(), path, init);
}
